import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Scissors, Boxes, PackageSearch, AlertTriangle, Hand, Clock } from "lucide-react";
import { statusMeta, priorityMeta, fmtDuration, fmtQty } from "@/pages/tasks/taskConstants";
import TaskDetail from "@/pages/tasks/TaskDetail";

export default function MyTasks() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [sel, setSel] = useState({});       // line_id -> bool, per pool
  const [report, setReport] = useState(null); // {kind}

  const load = useCallback(() => {
    api.get("/tasks/my").then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 45000); return () => clearInterval(t); }, [load]);

  const takeLines = async (poolKey, lineIds) => {
    if (!lineIds.length) return toast.error("Select at least one SKU");
    try { const r = await api.post("/tasks/claim-lines", { line_ids: lineIds }); toast.success("SKUs taken"); setSel({}); load(); setOpenId(r.data.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const takeTask = async (id) => {
    try { await api.post(`/tasks/${id}/take`); toast.success("Task taken"); load(); setOpenId(id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createBubble = async () => {
    try { const r = await api.post("/tasks/bubble", {}); toast.success("Bubble Cutting task created"); load(); setOpenId(r.data.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (err) return <div className="p-8 text-center text-slate-500" data-testid="mytasks-error">{err}</div>;
  if (!data) return <div className="p-8 text-center text-slate-400" data-testid="mytasks-loading">Loading…</div>;

  const section = (title, list, testid) => list.length > 0 && (
    <div className="mb-5" data-testid={testid}>
      <h2 className="text-sm font-semibold text-slate-500 mb-2">{title} <span className="text-slate-300">({list.length})</span></h2>
      <div className="grid gap-2 sm:grid-cols-2">{list.map((t) => <MyTaskCard key={t.id} t={t} onOpen={() => setOpenId(t.id)} />)}</div>
    </div>
  );

  return (
    <div data-testid="mytasks-page" className="max-w-5xl mx-auto">
      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="caption text-xs uppercase tracking-widest text-slate-400">Operations</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-0.5">My Tasks</h1>
          <p className="text-slate-500 text-sm font-mono">{data.employee_id} · {data.team || "—"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("task:bubble_self") && <Button data-testid="create-bubble-btn" onClick={createBubble} className="bg-fuchsia-600 hover:bg-fuchsia-700"><Scissors className="w-4 h-4 mr-1.5" />Bubble Cutting</Button>}
          {can("task:claim") && <Button variant="outline" data-testid="report-refill-btn" onClick={() => setReport({ kind: "refill" })}><Boxes className="w-4 h-4 mr-1.5" />Refill Request</Button>}
          {can("task:claim") && <Button variant="outline" data-testid="report-discrepancy-btn" onClick={() => setReport({ kind: "discrepancy" })}><AlertTriangle className="w-4 h-4 mr-1.5" />Discrepancy</Button>}
          {can("task:claim") && <Button variant="outline" data-testid="report-issue-btn" onClick={() => setReport({ kind: "issue" })}><PackageSearch className="w-4 h-4 mr-1.5" />Report Issue</Button>}
        </div>
      </header>

      {section("In Progress", data.in_progress, "section-in-progress")}
      {section("Ready to Resume", data.ready_to_resume, "section-ready")}
      {section("Paused / Taken", data.paused, "section-paused")}
      {section("Blocked", data.blocked, "section-blocked")}

      {/* Available work pools (multi-SKU) */}
      {data.available_pools.length > 0 && (
        <div className="mb-5" data-testid="section-available-pools">
          <h2 className="text-sm font-semibold text-slate-500 mb-2">Available Work (take SKUs)</h2>
          <div className="space-y-3">
            {data.available_pools.map((p) => (
              <div key={p.pool_key} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`pool-${p.pool_key}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={priorityMeta(p.priority).cls}>{priorityMeta(p.priority).label}</Badge>
                    <span className="font-semibold text-sm">{p.task_type_name}</span>
                    {p.warehouse_code && <span className="text-xs text-slate-500 font-mono">WH {p.warehouse_code}</span>}
                  </div>
                  <span className="text-xs text-slate-400">{p.line_count} SKU · {fmtQty(p.total_qty)} pcs</span>
                </div>
                <div className="divide-y border-y mb-2">
                  {p.lines.map((ln) => (
                    <label key={ln.id} className="flex items-center gap-2 py-1.5 text-sm" data-testid={`pool-line-${ln.sku}`}>
                      <Checkbox checked={!!sel[ln.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [ln.id]: v }))} data-testid={`pool-check-${ln.sku}`} />
                      <span className="font-mono font-semibold">{ln.sku}</span>
                      <span className="text-slate-500 text-xs flex-1 truncate">{ln.name}</span>
                      <span className="text-xs text-slate-400">{fmtQty(ln.expected_qty)}</span>
                    </label>
                  ))}
                </div>
                <Button size="sm" data-testid={`pool-take-${p.pool_key}`}
                  onClick={() => takeLines(p.pool_key, p.lines.filter((l) => sel[l.id]).map((l) => l.id))}
                  className="bg-sky-600 hover:bg-sky-700"><Hand className="w-4 h-4 mr-1.5" />Take Selected SKUs</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available single/team tasks */}
      {data.available_team_tasks.length > 0 && (
        <div className="mb-5" data-testid="section-available-tasks">
          <h2 className="text-sm font-semibold text-slate-500 mb-2">Available Tasks</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {data.available_team_tasks.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2" data-testid={`avail-task-${t.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Badge variant="outline" className={priorityMeta(t.priority).cls}>{priorityMeta(t.priority).label}</Badge><span className="font-semibold text-sm">{t.task_type_name}</span></div>
                  <div className="text-xs text-slate-500 truncate">{t.sku ? `${t.sku} · ` : ""}{t.destination_rack ? `→ ${t.destination_rack}` : ""}{t.notes}</div>
                </div>
                <Button size="sm" data-testid={`take-task-${t.id}`} onClick={() => takeTask(t.id)} className="bg-sky-600 hover:bg-sky-700">Take</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {section("Completed Today", data.completed_today, "section-completed")}

      {data.in_progress.length + data.ready_to_resume.length + data.paused.length + data.blocked.length +
        data.available_pools.length + data.available_team_tasks.length + data.completed_today.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 p-10 text-center text-slate-400" data-testid="mytasks-empty">No tasks right now.</div>
      )}

      <TaskDetail taskId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={load} mode="staff" />
      <ReportDialog report={report} onClose={() => setReport(null)} onDone={load} />
    </div>
  );
}

function MyTaskCard({ t, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`mytask-${t.id}`}
      className="text-left rounded-xl border border-slate-200 bg-white p-3 hover:border-sky-300 transition-colors">
      <div className="flex items-center justify-between gap-2 mb-1">
        <Badge variant="outline" className={priorityMeta(t.priority).cls}>{priorityMeta(t.priority).label}</Badge>
        <Badge variant="outline" className={statusMeta(t.status).cls}>{statusMeta(t.status).label}</Badge>
      </div>
      <div className="font-semibold text-sm">{t.task_type_name}</div>
      <div className="text-xs text-slate-500 font-mono">{t.task_no}{t.warehouse_code ? ` · WH ${t.warehouse_code}` : ""}</div>
      <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDuration(t.active_seconds)} active{t.completed_qty ? ` · ${fmtQty(t.completed_qty)} done` : ""}</div>
    </button>
  );
}

function ReportDialog({ report, onClose, onDone }) {
  const [f, setF] = useState({});
  useEffect(() => { setF({}); }, [report]);
  if (!report) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    try {
      if (report.kind === "refill") await api.post("/tasks/refill-request", { sku: f.sku, destination_rack: f.rack, expected_qty: f.qty ? Number(f.qty) : null, note: f.note });
      else if (report.kind === "discrepancy") await api.post("/tasks/rack-discrepancy", { sku: f.sku, rack: f.rack, expected_qty: f.qty ? Number(f.qty) : null, note: f.note });
      else await api.post("/ops/issues", { category: f.category || "other", order_ref: f.order_ref, note: f.note });
      toast.success("Submitted"); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const titles = { refill: "Daily Rack Refill Request", discrepancy: "Rack Stock Discrepancy", issue: "Report Order Issue" };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid={`report-dialog-${report.kind}`}>
        <DialogHeader><DialogTitle>{titles[report.kind]}</DialogTitle><DialogDescription>Operational record. Inventory totals are not changed.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {report.kind !== "issue" && <><Label className="text-xs">SKU</Label><Input data-testid="report-sku" value={f.sku || ""} onChange={(e) => set("sku", e.target.value)} /></>}
          {report.kind === "refill" && <><Label className="text-xs">Destination (Picking) Rack</Label><Input data-testid="report-rack" value={f.rack || ""} onChange={(e) => set("rack", e.target.value)} /></>}
          {report.kind === "discrepancy" && <><Label className="text-xs">Rack</Label><Input data-testid="report-rack" value={f.rack || ""} onChange={(e) => set("rack", e.target.value)} /></>}
          {report.kind !== "issue" && <><Label className="text-xs">Qty (optional)</Label><Input type="number" data-testid="report-qty" value={f.qty || ""} onChange={(e) => set("qty", e.target.value)} /></>}
          <Label className="text-xs">{report.kind === "discrepancy" ? "Observed issue" : "Note"}</Label>
          <Textarea data-testid="report-note" value={f.note || ""} onChange={(e) => set("note", e.target.value)} rows={2} />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="report-submit" onClick={submit}>Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
